import React from "react";
import ReactDOMServer from "react-dom/server";

/**
 * Update the inline styles of `el` with the entries of `styles`.
 *
 * This exists even though redom.setStyle exists because that uses `el.style[k]
 * = v`, which doesn't support CSS custom properties (i.e. variables), at least
 * in FireFox at time of writing. This instead uses setProperty which does
 * support them.
 */
export function setStyles(el: HTMLElement, styles: React.CSSProperties): void {
	for (const [k, v] of Object.entries(styles)) {
		el.style.setProperty(k, v);
	}
}

/**
 * Render a react element into a DOM node
 *
 * Watch out for id attributes in the copied node.
 */
export function render(el: JSX.Element): Node {
	const node = document.createElement("div");
	node.innerHTML = ReactDOMServer.renderToStaticMarkup(el);
	const result = node.firstChild;
	if (!(result instanceof Node)) throw new Error();
	return result;
}

/**
 * Raised by getHTMLElement if the selected element doesn't exist or if it's
 * not an instance of HTMLElement.
 */
export class NotAnHTMLElement extends Error {}

/**
 * Like querySelector, but asserts that the selected element exists and that
 * it's an HTMLElement (as opposed to an SVGElement or TextNode, for example).
 */
export function getHTMLElement(root: HTMLElement, selector: string): HTMLElement {
	const el = root.querySelector(selector);
	if (!(el instanceof HTMLElement)) throw new NotAnHTMLElement(el?.nodeName);
	return el;
}

/**
 * A wrapper around Node.cloneNode that ensures that the output is the same
 * type as the input (instead of always Node).
 */
export function cloneNode<T extends Node>(el: T, deep = true): T {
	const cloned = el.cloneNode(deep);
	// Make sure it's an instance of the same class as `el`.
	if (!(cloned instanceof el.constructor)) {
		throw new Error("Failed to clone node");
	}
	return cloned as T;
}

/**
 * Get a single element by class name.
 *
 * Ensures that there is either 0 or 1 element with a given class name.
 */
export function getElementByClassName(root: Element, names: string): Element | null {
	const elements = root.getElementsByClassName(names);
	if (elements.length > 1) {
		throw new RangeError();
	}
	return elements[0] ?? null;
}

export interface DOMComponent<TData> {
	update(data: TData): void;
	deinit?(): void;
}
